import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MISSION_STATUS_LABELS, api, useApiMutation, useApiQuery } from "../../api";
import type { MissionForConsultant, TimesheetSummary } from "../../api";
import { Badge, Button, Card, EmptyState, Field, Skeleton } from "../../components";
import { AsyncBoundary, PageHeader, consultantFeuillePath } from "../../shell";
import { FeuillesTable } from "./FeuillesTable";
import { messageFeuille } from "./messages";
import { derniersMois, formatMois, libelleMois, moisCourant } from "./mois";
import styles from "./Feuilles.module.css";

/**
 * L'accueil du consultant : ses missions, et pour chacune la feuille du mois
 * à ouvrir ou à reprendre.
 *
 * Aucun montant nulle part sur cet écran, ni sur les suivants : les types
 * reçus n'en ont pas, et l'écran n'en fabrique pas.
 */
export function ConsultantFeuillesPage() {
  const missions = useApiQuery((o) => api.listMyMissions(o), []);
  const feuilles = useApiQuery((o) => api.listMyTimesheets(o), []);

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Espace consultant"
        title="Mes feuilles de temps"
        description="Une feuille par mission et par mois : déclarez vos demi-journées au fil de l'eau, puis soumettez-la. Le client et votre commercial sont prévenus."
      />

      <AsyncBoundary
        state={missions}
        errorTitle="Missions illisibles"
        skeleton={
          <div className={styles.missions} aria-busy="true">
            <Skeleton variant="block" height="8rem" />
            <Skeleton variant="block" height="8rem" />
          </div>
        }
        isEmpty={(d) => d.missions.length === 0}
        empty={
          <EmptyState
            title="Aucune mission affectée"
            description="Votre commercial vous affectera une mission ; elle apparaîtra ici avec sa feuille du mois."
          />
        }
      >
        {(donnees) => (
          <div className={styles.missions}>
            {donnees.missions.map((m) => (
              <MissionCarte key={m.id} mission={m} feuilles={feuilles.data?.timesheets ?? []} onOuverte={feuilles.reload} />
            ))}
          </div>
        )}
      </AsyncBoundary>
    </div>
  );
}

function MissionCarte({
  mission,
  feuilles,
  onOuverte,
}: {
  mission: MissionForConsultant;
  feuilles: TimesheetSummary[];
  onOuverte: () => void;
}) {
  const navigate = useNavigate();
  const ouverture = useApiMutation(api.openTimesheet);
  const [mois, setMois] = useState(formatMois(moisCourant()));
  const ouverte = mission.status === "ordered";
  const miennes = feuilles.filter((f) => f.mission_id === mission.id);
  const dejaOuverts = new Set(miennes.map((f) => f.period_month));

  async function ouvrir() {
    const feuille = await ouverture.run({ mission_id: mission.id, period_month: mois });
    if (feuille === null) {
      return;
    }
    onOuverte();
    void navigate(consultantFeuillePath(feuille.id));
  }

  return (
    <Card
      as="article"
      title={mission.label}
      actions={
        <Badge tone={ouverte ? "info" : "neutral"} dot>
          {MISSION_STATUS_LABELS[mission.status]}
        </Badge>
      }
    >
      <div className={styles.fiche}>
        {ouverte ? (
          // L'aide est sous la ligne, pas sous le champ : dans le champ, elle
          // ferait descendre le bas du bloc, et le bouton, aligné sur ce bas,
          // se retrouverait plus bas que la liste.
          <div className={styles.ouverture}>
            <Field label="Mois">
              {(commande) => (
                <select {...commande} value={mois} onChange={(e) => setMois(e.target.value)} disabled={ouverture.pending}>
                  {derniersMois(12).map((m) => (
                    <option key={formatMois(m)} value={formatMois(m)}>
                      {libelleMois(m)}
                      {dejaOuverts.has(formatMois(m)) ? " · déjà ouverte" : ""}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Button variant="primary" onClick={() => void ouvrir()} loading={ouverture.pending}>
              {dejaOuverts.has(mois) ? "Reprendre la feuille" : "Ouvrir la feuille"}
            </Button>
            <p className={styles.aideLigne}>Le mois courant, ou un mois passé.</p>
          </div>
        ) : (
          <p className={styles.mention}>Mission close : les feuilles existantes restent consultables, aucune ne s'ouvre.</p>
        )}
        {ouverture.error !== null && (
          <p className={styles.refus} role="alert">
            {messageFeuille(ouverture.error)}
          </p>
        )}
        <FeuillesTable
          rows={miennes}
          lienVers={consultantFeuillePath}
          montants={false}
          consultant={false}
          caption={`Feuilles de la mission ${mission.label}`}
          vide={{ titre: "Aucune feuille", description: "Ouvrez la feuille du mois pour commencer." }}
        />
      </div>
    </Card>
  );
}
